import { useState, useEffect, useCallback, useMemo } from "react"
import type {
  AdditionalConfigField,
  ConfigFormState,
  ConfigFormActions,
  PrefixOption,
} from "../types"

interface UseConfigFormStateOptions {
  additionalConfigSchema: AdditionalConfigField[]
  existingMetadata: Record<string, unknown> | null
}

/**
 * Helper to find the default prefix for a property type option
 */
function getDefaultPrefixForOption(
  prefixOptions: PrefixOption[] | undefined,
): string | null {
  if (!prefixOptions || prefixOptions.length === 0) return null
  const defaultOption = prefixOptions.find(opt => opt.default)
  return defaultOption?.value ?? prefixOptions[0].value
}

/**
 * Hook to manage form state for additional config form
 * Handles form data, prefixes, tags, and error state
 */
export function useConfigFormState({
  additionalConfigSchema,
  existingMetadata,
}: UseConfigFormStateOptions): ConfigFormState & ConfigFormActions {
  const [formData, setFormData] = useState<Record<string, unknown>>({})
  const [selectedPrefixes, setSelectedPrefixes] = useState<
    Record<string, string>
  >({})
  const [tagInputValues, setTagInputValues] = useState<Record<string, string>>(
    {},
  )
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  // Build a map of property_type_field -> config for quick lookup
  const propertyTypeFieldMap = useMemo(() => {
    const map = new Map<string, AdditionalConfigField>()
    additionalConfigSchema.forEach(config => {
      if (config.property_type_field) {
        map.set(config.property_type_field, config)
      }
    })
    return map
  }, [additionalConfigSchema])

  // Pre-fill form with existing metadata or initialize defaults
  useEffect(() => {
    if (existingMetadata && Object.keys(existingMetadata).length > 0) {
      setFormData(existingMetadata)
      if (existingMetadata.selected_prefixes) {
        setSelectedPrefixes(
          existingMetadata.selected_prefixes as Record<string, string>,
        )
      }
    } else {
      const initialData: Record<string, unknown> = {}
      const initialPrefixes: Record<string, string> = {}

      additionalConfigSchema.forEach((config: AdditionalConfigField) => {
        // Handle property_type_field defaults
        if (config.property_type_field && config.property_type_options) {
          const defaultOption = Object.entries(
            config.property_type_options,
          ).find(([_, option]) => option.default === true)
          if (defaultOption) {
            initialData[config.property_type_field] = defaultOption[0]
            const fieldName = config.configuration_path.split(".").pop()
            if (fieldName) {
              const defaultPrefix = getDefaultPrefixForOption(
                defaultOption[1].prefix_options,
              )
              if (defaultPrefix) {
                initialPrefixes[fieldName] = defaultPrefix
              }
            }
          }
        }

        // Handle standalone dropdown defaults
        if (!config.property_type_field && config.property_type_options) {
          const fieldName = config.configuration_path.split(".").pop()
          if (fieldName) {
            const defaultOption = Object.entries(
              config.property_type_options,
            ).find(([_, option]) => option.default === true)
            if (defaultOption) {
              initialData[fieldName] = defaultOption[0]
            }
          }
        }
      })

      if (Object.keys(initialData).length > 0) {
        setFormData(initialData)
      }
      if (Object.keys(initialPrefixes).length > 0) {
        setSelectedPrefixes(initialPrefixes)
      }
    }
  }, [existingMetadata, additionalConfigSchema])

  const handleInputChange = useCallback(
    (fieldName: string, value: string | string[]) => {
      setFormData(prev => ({
        ...prev,
        [fieldName]: value,
      }))

      // If this is a property_type_field change, update the associated prefix
      const config = propertyTypeFieldMap.get(fieldName)
      if (config?.property_type_options) {
        const selectedOption = config.property_type_options[value as string]
        const targetFieldName = config.configuration_path.split(".").pop()
        if (targetFieldName && selectedOption?.prefix_options) {
          const defaultPrefix = getDefaultPrefixForOption(
            selectedOption.prefix_options,
          )
          if (defaultPrefix) {
            setSelectedPrefixes(prev => ({
              ...prev,
              [targetFieldName]: defaultPrefix,
            }))
          }
        }
      }

      if (errorMessage) {
        setErrorMessage(null)
      }
    },
    [propertyTypeFieldMap, errorMessage],
  )

  const handlePrefixChange = useCallback(
    (fieldName: string, prefix: string) => {
      setSelectedPrefixes(prev => ({
        ...prev,
        [fieldName]: prefix,
      }))
    },
    [],
  )

  const getTags = useCallback(
    (fieldName: string): string[] => {
      const value = formData[fieldName]
      if (Array.isArray(value)) {
        return value
      }
      if (typeof value === "string" && value.trim()) {
        return value
          .split(",")
          .map(s => s.trim())
          .filter(s => s.length > 0)
      }
      return []
    },
    [formData],
  )

  const addTag = useCallback(
    (fieldName: string, newTag: string) => {
      // Several connector guides tell the customer to comma-separate multiple
      // ids ("12345,67890"), so accept that as well as one-at-a-time entry.
      // Without the split the whole string lands as one malformed array item
      // and nothing downstream rejects it.
      const incomingTags = newTag
        .split(",")
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0)
      if (!incomingTags.length) return

      const currentTags = getTags(fieldName)

      // Check maxItems constraint from schema
      const config = additionalConfigSchema.find(
        c => c.configuration_path.split(".").pop() === fieldName,
      )
      if (
        config?.maxItems !== undefined &&
        currentTags.length >= config.maxItems
      ) {
        return
      }

      const nextTags = [...currentTags]
      for (const tag of incomingTags) {
        if (
          config?.maxItems !== undefined &&
          nextTags.length >= config.maxItems
        ) {
          break
        }
        if (!nextTags.includes(tag)) {
          nextTags.push(tag)
        }
      }

      if (nextTags.length !== currentTags.length) {
        setFormData(prev => ({
          ...prev,
          [fieldName]: nextTags,
        }))
      }
      setTagInputValues(prev => ({
        ...prev,
        [fieldName]: "",
      }))
    },
    [getTags, additionalConfigSchema],
  )

  const removeTag = useCallback(
    (fieldName: string, indexToRemove: number) => {
      const currentTags = getTags(fieldName)
      setFormData(prev => ({
        ...prev,
        [fieldName]: currentTags.filter((_, index) => index !== indexToRemove),
      }))
    },
    [getTags],
  )

  const handleTagInputChange = useCallback(
    (fieldName: string, value: string) => {
      setTagInputValues(prev => ({
        ...prev,
        [fieldName]: value,
      }))
    },
    [],
  )

  return {
    // State
    formData,
    selectedPrefixes,
    tagInputValues,
    errorMessage,
    // Actions
    handleInputChange,
    handlePrefixChange,
    handleTagInputChange,
    addTag,
    removeTag,
    getTags,
    setErrorMessage,
  }
}
